import { motion } from "framer-motion";
import { ChevronRight, Activity, TrendingUp, Target, Award, Repeat } from "lucide-react";

const RANK_ICON = {
  Recluta: "https://customer-assets.emergentagent.com/job_honor-platform-2/artifacts/ejd62dnk_recluta%203d.png",
  Ranger: "https://customer-assets.emergentagent.com/job_honor-platform-2/artifacts/5v1uf4wc_ranger%203d.png",
  Tango: "https://customer-assets.emergentagent.com/job_honor-platform-2/artifacts/gwlpim9i_tango%203d.png",
  Charlie: "https://customer-assets.emergentagent.com/job_honor-platform-2/artifacts/ilrz6znl_charlei%203d.png",
  Delta: "https://customer-assets.emergentagent.com/job_honor-platform-2/artifacts/d2nxru6a_delta%203d.png",
  "Team Six": "https://customer-assets.emergentagent.com/job_honor-platform-2/artifacts/5fefdau2_team%20six%203d.png",
  Falcons: "https://customer-assets.emergentagent.com/job_honor-platform-2/artifacts/4hm9robs_falcons%203d.png",
};

const RANKS = [
  {
    name: "Recluta",
    metaMin: null,
    metaIdeal: null,
    requirement: "Punto de partida",
    features: [
      ["Acceso a la ", { k: "plataforma educativa" }, "."],
      ["Señales automatizadas de ", { k: "traders élite" }, " (modo vista)."],
      ["Acceso a clases grabadas ", { k: "y a bitácora de RECLUTA" }, "."],
    ],
  },
  {
    name: "Ranger",
    metaMin: "6%",
    metaIdeal: "940 USD",
    requirement: "Bitácora + 6% cuenta real",
    features: [
      ["Bitácora de misiones ", { k: "RANGER + RECLUTA" }, "."],
      ["Señales de los traders ", { k: "FALCON" }, "."],
      ["Clases y ", { k: "Trading en vivo" }, "."],
      ["Pool de recompensa exclusivo ", { k: "RANGER" }, " (2 USD por soldado activo)."],
    ],
  },
  {
    name: "Tango",
    metaMin: "7%",
    metaIdeal: "1.920 USD",
    requirement: "Bitácora + 7% cuenta real",
    features: [
      ["Incluye acceso a ", { k: "rangos anteriores" }, "."],
      ["Bitácora de misiones ", { k: "TANGO" }, "."],
      ["Acceso a indicador ", { k: "HONOR SNIPER" }, "."],
      ["Pool de recompensa exclusivo ", { k: "TANGO" }, " (2 USD por soldado activo)."],
    ],
  },
  {
    name: "Charlie",
    metaMin: "8%",
    metaIdeal: "5.500 USD",
    requirement: "Bitácora + 8% cuenta real",
    features: [
      ["Incluye acceso a ", { k: "rangos anteriores" }, "."],
      ["Bitácora de misiones ", { k: "CHARLIE" }, "."],
      ["Pool de recompensa exclusivo ", { k: "CHARLIE" }, " (2 USD por soldado activo)."],
      ["Acceso al ", { k: "CopyTrade" }, " de la academia."],
    ],
  },
  {
    name: "Delta",
    metaMin: "9%",
    metaIdeal: "9.500 USD",
    requirement: "Bitácora + 9% cuenta real",
    features: [
      ["Incluye acceso a ", { k: "rangos anteriores" }, "."],
      ["Bitácora de misiones ", { k: "DELTA" }, "."],
      ["Entrenamientos exclusivos de ", { k: "psicotrading" }, " y ", { k: "coaching" }, " con ", { k: "traders élite" }, "."],
      ["Pool de recompensa exclusivo ", { k: "DELTA" }, " (3 USD por soldado activo)."],
    ],
  },
  {
    name: "Team Six",
    metaMin: "10%",
    metaIdeal: "19.000 USD",
    requirement: "Bitácora + 10% cuenta real",
    features: [
      ["Incluye acceso a ", { k: "rangos anteriores" }, "."],
      ["Bitácora de misiones ", { k: "TEAM SIX" }, "."],
      [{ k: "Mentorías" }, " exclusivas ", { k: "personalizadas" }, " con ", { k: "traders élite" }, " y sesiones de ", { k: "Psicotrading" }, "."],
      ["Pool de recompensa exclusivo ", { k: "TEAM SIX" }, " (4 USD por soldado activo)."],
    ],
  },
  {
    name: "Falcons",
    metaMin: "8%",
    metaIdeal: "50.000 USD",
    requirement: "Por mérito · STAFF",
    features: [
      ["Incluye acceso a ", { k: "rangos anteriores" }, "."],
      ["Bitácora de misiones ", { k: "FALCON" }, "."],
      ["Prueba de acceso al ", { k: "staff" }, " de ", { k: "Honor" }, "."],
      ["Pool de recompensa exclusivo ", { k: "FALCON" }, " (5 USD por soldado activo)."],
    ],
    highlight: true,
  },
];

const Feature = ({ parts }) => (
  <li className="flex items-start gap-3 p-3 rounded-xl bg-[#1A2E1A]/30 border border-[#3E4D36]/40 hover:border-[#BEDD7D]/40 hover:bg-[#1A2E1A]/60 transition-all">
    <span className="mt-0.5 w-6 h-6 shrink-0 rounded-md bg-[#BEDD7D] flex items-center justify-center">
      <ChevronRight className="w-3.5 h-3.5 text-black" strokeWidth={3} />
    </span>
    <span className="text-[13px] font-body leading-snug text-white/90">
      {parts.map((p, i) =>
        typeof p === "string" ? (
          <span key={i}>{p}</span>
        ) : (
          <strong key={i} className="text-[#BEDD7D] font-semibold">
            {p.k}
          </strong>
        )
      )}
    </span>
  </li>
);

const RankCard = ({ rank, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.5, delay: (index % 4) * 0.08 }}
    className={`relative flex flex-col rounded-2xl overflow-hidden border transition-all ${
      rank.highlight
        ? "border-[#BEDD7D]/60 bg-gradient-to-b from-[#1A2E1A]/60 to-black shadow-[0_0_40px_rgba(190,221,125,0.15)]"
        : "border-[#3E4D36]/50 bg-gradient-to-b from-[#0a0f0a] to-black hover:border-[#BEDD7D]/40"
    }`}
    data-testid={`rank-card-${rank.name.toLowerCase().replace(/ /g, "-")}`}
  >
    <div className="relative p-5 border-b border-[#3E4D36]/40 overflow-hidden">
      <img
        src={RANK_ICON[rank.name]}
        alt=""
        aria-hidden
        className="absolute -right-4 -bottom-4 w-32 h-32 opacity-15 blur-[0.5px] pointer-events-none"
      />
      <div className="relative flex items-center gap-3">
        <div className="w-14 h-14 shrink-0 rounded-xl bg-black/60 border border-[#3E4D36]/60 flex items-center justify-center overflow-hidden">
          <img
            src={RANK_ICON[rank.name]}
            alt={rank.name}
            className="w-12 h-12 object-contain"
            loading="lazy"
          />
        </div>
        <div>
          <div className="text-[10px] font-display uppercase tracking-[0.3em] text-[#BEDD7D]">
            0{index + 1}
          </div>
          <h3 className="font-display font-bold text-2xl uppercase tracking-tight text-white leading-none mt-1">
            {rank.name}
          </h3>
        </div>
      </div>

      {/* Requirement pill (replaces inscription) */}
      <div className="mt-4 px-3 py-2.5 rounded-lg bg-black/60 border border-[#3E4D36]/60">
        <div className="text-[9px] font-display uppercase tracking-widest text-white/50">
          Se gana por
        </div>
        <div className="font-display font-bold text-[#BEDD7D] text-sm mt-0.5">
          {rank.requirement}
        </div>
      </div>

      {(rank.metaMin || rank.metaIdeal) && (
        <div className="mt-2 grid grid-cols-2 gap-2">
          <div className="px-2.5 py-1.5 rounded-md bg-[#1A2E1A]/60 border border-[#3E4D36]/50">
            <div className="text-[8px] font-display uppercase tracking-widest text-white/50">
              Meta mín.
            </div>
            <div className="font-display font-bold text-[#BEDD7D] text-xs mt-0.5">
              {rank.metaMin}
            </div>
          </div>
          <div className="px-2.5 py-1.5 rounded-md bg-[#1A2E1A]/60 border border-[#3E4D36]/50">
            <div className="text-[8px] font-display uppercase tracking-widest text-white/50">
              Meta ideal
            </div>
            <div className="font-display font-bold text-[#BEDD7D] text-xs mt-0.5">
              {rank.metaIdeal}
            </div>
          </div>
        </div>
      )}
    </div>

    <ul className="p-3 space-y-2 flex-1">
      {rank.features.map((parts, idx) => (
        <Feature key={idx} parts={parts} />
      ))}
    </ul>
  </motion.div>
);

const CRITERIA = [
  { icon: Activity, label: "Actividad" },
  { icon: TrendingUp, label: "6%-10% cuenta real" },
  { icon: Repeat, label: "Consistencia" },
  { icon: Target, label: "Evaluaciones" },
  { icon: Award, label: "Bitácoras + Misiones" },
];

export default function Ranks() {
  return (
    <section
      id="rangos"
      className="relative py-24 md:py-32 bg-black overflow-hidden"
      data-testid="ranks-section"
    >
      <div className="absolute inset-0 radial-green opacity-40 pointer-events-none" />

      <div className="relative max-w-[1600px] mx-auto px-4 md:px-8">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <span className="text-xs font-display uppercase tracking-[0.4em] text-[#BEDD7D]">
            / Sistema Militar de Rangos
          </span>
          <h2 className="mt-6 font-display font-bold text-4xl md:text-6xl uppercase tracking-tight leading-none">
            Avanza por <span className="italic text-[#BEDD7D]">mérito</span>.
            <br />
            No por <span className="italic text-[#BEDD7D]">pagar más</span>.
          </h2>
          <p className="mt-6 text-white/60 font-body">
            7 rangos que se ganan por desempeño real, no por suscripción. Tu mensualidad te da
            educación; tu disciplina te da el rango.
          </p>
        </div>

        {/* 4 + 3 layout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {RANKS.slice(0, 4).map((rank, i) => (
            <RankCard key={rank.name} rank={rank} index={i} />
          ))}
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:max-w-[75%] lg:mx-auto">
          {RANKS.slice(4).map((rank, i) => (
            <RankCard key={rank.name} rank={rank} index={i + 4} />
          ))}
        </div>

        {/* ¿Cómo se gana el rango? */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="mt-16 relative rounded-3xl border border-[#BEDD7D]/40 bg-gradient-to-br from-[#1A2E1A]/40 to-black p-8 md:p-12 overflow-hidden"
          data-testid="ranks-criteria"
        >
          <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
          <div className="relative grid lg:grid-cols-3 gap-10 items-center">
            <div className="lg:col-span-1">
              <span className="text-xs font-display uppercase tracking-[0.4em] text-[#BEDD7D]">
                / Cómo se gana
              </span>
              <h3 className="mt-4 font-display font-bold text-3xl md:text-4xl uppercase tracking-tight leading-none">
                El rango se <span className="italic text-[#BEDD7D]">gana</span>.
              </h3>
              <p className="mt-4 text-white/70 font-body text-sm leading-relaxed">
                Cada rango se desbloquea superando la <strong className="text-[#BEDD7D]">bitácora del rango</strong> y alcanzando el <strong className="text-[#BEDD7D]">porcentaje mínimo</strong> (6%-10%) en cualquier cuenta real conectada a nuestros brokers afiliados. Tu progreso depende de tu desempeño, no de tu billetera.
              </p>
            </div>

            <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3">
              {CRITERIA.map((c) => (
                <div
                  key={c.label}
                  className="flex items-center gap-3 p-4 rounded-xl bg-black/60 border border-[#3E4D36]/50 hover:border-[#BEDD7D]/50 transition-colors"
                  data-testid={`rank-criteria-${c.label.toLowerCase().replace(/ /g, "-")}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-[#1A2E1A] border border-[#BEDD7D]/30 flex items-center justify-center shrink-0">
                    <c.icon className="w-4 h-4 text-[#BEDD7D]" />
                  </div>
                  <span className="font-display font-bold text-sm uppercase tracking-wide text-white">
                    {c.label}
                  </span>
                </div>
              ))}

              <div className="col-span-2 md:col-span-3 lg:col-span-3 mt-2 p-4 rounded-xl bg-[#BEDD7D]/10 border border-[#BEDD7D]/40 text-center">
                <div className="font-display font-bold text-sm uppercase tracking-[0.2em] text-[#BEDD7D]">
                  NO se gana por pagar más
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
